<template>
  <div class="py-8">
    <v-app-bar flat color="transparent">
      <app-bar-nav-icon />
      <v-toolbar-title class="breadcrumbs d-flex align-center">
        <v-icon class="text-light-blue">{{ icons.arrowLeft }}</v-icon>
        <router-link class="text-decoration--none" :to="{ name: 'discoveries' }"
          >Discover Menus</router-link
        >
      </v-toolbar-title>
    </v-app-bar>

    <div class="ma-4">
      <div class="display-2 font-weight-bold my-6 text-capitalize">
        {{ displayName }}
      </div>

      <div class="d-flex">
        <div>
          <upload-photo
            :image-source="form.cover_photo ? form.cover_photo.url : null"
            @change="onChangeImage"
            @error="
              form.$setErrors({ cover_photo: ['The file size is too large'] })
            "
          />
          <div
            class="v-messages theme--light error--text"
            v-if="form.$getError('cover_photo')"
          >
            {{ form.$getError('cover_photo')[0] }}
          </div>
        </div>
      </div>

      <div class="my-6">
        <v-form ref="form" class="discovery-form">
          <label class="text-field-label">Name</label>
          <v-text-field
            flat
            solo
            required
            class="mt-2"
            v-model="form.discovery_name"
            :error-messages="form.$getError('discovery_name')"
            :loading="loading"
          ></v-text-field>

          <div class="d-flex mt-4">
            <v-btn
              v-if="form.id"
              color="error"
              class="mr-4 px-6"
              height="40px"
              :loading="form.$busy"
              @click="deleteDialog = true"
              >Delete</v-btn
            >

            <v-spacer></v-spacer>

            <v-btn
              color="primary"
              class="px-6"
              height="40px"
              @click="discovery ? updateDiscoveryDetails() : addDiscovery()"
              :loading="form.$busy"
              >Save</v-btn
            >
          </div>
        </v-form>

        <v-dialog v-model="deleteDialog" max-width="290" v-if="discovery">
          <v-card>
            <v-card-title class="headline">Confirm Delete</v-card-title>

            <v-card-text>
              <div v-if="discovery.pages_count > 0">
                Warning: {{ discovery.pages_count }} page/s under this discover
                menu will also be deleted.
              </div>
              <div v-else>
                There are no existing pages under this discover menu.
              </div>
              <br />Do you want to continue?
            </v-card-text>

            <v-card-actions>
              <v-btn text @click="deleteDialog = false" :loading="form.$busy"
                >Cancel</v-btn
              >

              <v-spacer></v-spacer>

              <v-btn
                color="red"
                text
                @click="removeDiscovery"
                :loading="form.$busy"
                >Yes, Proceed</v-btn
              >
            </v-card-actions>
          </v-card>
        </v-dialog>
      </div>
    </div>

    <v-overlay absolute :value="loading" opacity="0.2">
      <v-progress-circular
        indeterminate
        color="primary"
        size="64"
      ></v-progress-circular>
    </v-overlay>
    <v-snackbar v-model="snackbar.show" right :color="snackbar.color">
      {{ snackbar.message }}
      <v-btn text @click="snackbar.show = false">OK</v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import { mdiChevronLeft, mdiCheck, mdiAlertCircleOutline } from '@mdi/js'
import { mapState, mapActions, mapMutations } from 'vuex'
import AppBarNavIcon from '@/layouts/shared/AppBarNavIcon'
import UploadPhoto from '@/components/UploadPhoto'
import Form from '@/utils/form'

export default {
  name: 'DiscoveryDetails',

  components: {
    AppBarNavIcon,
    UploadPhoto,
  },

  data() {
    return {
      icons: {
        arrowLeft: mdiChevronLeft,
        check: mdiCheck,
        alert: mdiAlertCircleOutline,
      },
      loading: false,
      deleteDialog: false,
      form: new Form({
        cover_photo: '',
        discovery_name: '',
      }),
      snackbar: {
        show: false,
        message: null,
        color: '',
      },
      showModal: false,
    }
  },

  computed: {
    ...mapState({
      discovery: (state) => state.discovery.discoveryDetails,
      authUser: (state) => state.auth.user,
    }),
    displayName() {
      return this.form.discovery_name
    },
  },

  methods: {
    ...mapActions({
      getDiscoveryDetails: 'discovery/getDiscoveryDetails',
      createDiscovery: 'discovery/createDiscovery',
      updateDiscovery: 'discovery/updateDiscovery',
      deleteDiscovery: 'discovery/deleteDiscovery',
    }),

    ...mapMutations({
      clearDiscoveryDetails: 'discovery/clearDiscoveryDetails',
    }),

    async getDiscovery() {
      this.loading = true
      await this.getDiscoveryDetails(this.$route.params.id)
      this.form = new Form(this.discovery)
      this.loading = false
    },

    async updateDiscoveryDetails() {
      this.form.$busy = true
      this.form.$clearErrors()
      this.updateDiscovery(this.getFormData())
        .then(() => {
          this.form.$busy = false
          this.imageChanged = false
          this.showSnackbar(
            'Discovery details successfully updated!',
            'success'
          )
          this.$router.replace({ name: 'discoveries' })
        })
        .catch((err) => {
          this.form.$busy = false
          if (err.response.status === 422) {
            this.form.$setErrors(err.response.data.errors)
          }
        })
    },

    async addDiscovery() {
      this.form.$busy = true
      this.form.$clearErrors()
      this.createDiscovery(this.getFormData())
        .then(() => {
          this.form.$busy = false
          this.imageChanged = false
          this.showSnackbar('Discovery successfully added!', 'success')
          this.$router.replace({ name: 'discoveries' })
        })
        .catch((err) => {
          this.form.$busy = false
          if (err.response.status === 422) {
            this.form.$setErrors(err.response.data.errors)
          }
        })
    },

    removeDiscovery() {
      this.form.$busy = true
      this.deleteDiscovery(this.form.id)
        .then(() => {
          this.form.$busy = false
          this.deleteDialog = false
          this.showSnackbar('Discovery deleted successfully!')
          this.$router.replace('/discoveries')
        })
        .catch((err) => {
          this.form.$busy = false
          if (err.response.status === 404) {
            this.deleteDialog = false
            this.showSnackbar(
              'The discover menu you are trying to delete is no longer existing.',
              'error'
            )
          }
        })
    },

    onChangeImage(blob) {
      this.imageChanged = true
      this.form.$clearErrors()
      this.form.cover_photo = blob
    },

    getFormData() {
      let data = new FormData()
      data.append('id', this.form.id)
      data.append('discovery_name', this.form.discovery_name)
      if (this.imageChanged) {
        data.append('cover_photo', this.form.cover_photo)
      } else {
        data.delete('cover_photo')
      }
      return data
    },

    showSnackbar(message, color) {
      this.snackbar.message = message
      this.snackbar.show = true
      this.snackbar.color = color
    },
  },

  created() {
    if (this.$router.currentRoute.name === 'discovery.details') {
      this.getDiscovery()
    }
  },

  destroyed() {
    this.clearDiscoveryDetails()
  },

  watch: {
    $route() {
      this.getDiscovery()
    },
  },
}
</script>

<style lang="scss" scoped>
.discovery-form {
  width: 400px;

  @media (max-width: 420px) {
    width: 100%;
  }
}
</style>
